import { useLocation, useNavigate } from 'react-router-dom';
import { useLoginWithForm } from '../hooks/useLoginWithForm';
import { useEmailTypoDetection } from '../hooks/useEmailTypoDetection';
import React from 'react';
import { AUTENTIFICARE_COD_ACTIVA } from '../utils/otp-flag';
import { AUTH_PAROLA_ACTIVA } from '../utils/parola-flag';
import { variantaLogin } from '../utils/varianta-login';
import { esteEmailValid, stareVerificareCod } from '../utils/otp-cod';
import { reduceTrimitere, STARE_TRIMITERE_INITIALA } from '../utils/stare-trimitere';
import { cereCodOtp } from '../api/otp.api';
import { getErrorMessage } from '../../../core/api/response-handler';
import { AlegereElevi, AlegereIntrare, InapoiLaAlegere } from '../components/AlegereIntrare';
import { INTRARE_COD_SCOALA_ACTIVA } from '../utils/intrare-cod-flag';
import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { PasswordInput } from '../../../components/ui/password-input';
import { Separator } from '../../../components/ui/separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import PageWrap from '../../../components/auth/page-wrap';
import { FEATURE_FLAGS } from '../../../core/config/feature-flags';
import { InParteneriatCu } from '../../../shared/components/Parteneri';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { isGoogleSignInAvailable } from '../utils/google-sign-in';

/**
 * 🔴 Ecranul de logare are DOUA variante, alese de `variantaLogin` (functie pura, testata).
 *
 * Decizia owner-ului din 18.09: nu mai exista conceptul de parola, pentru nimeni. Codul construit
 * nu se arunca — varianta cu parola ramane mai jos, intacta, sub `VITE_AUTH_PAROLA` (implicit
 * oprit). Doua componente in acelasi fisier, nu `&&`-uri prin 370 de linii: hook-urile variantei
 * cu parola (`useLoginWithForm`, `useEmailTypoDetection`) nu se mai executa deloc cand ea nu se
 * randeaza, iar diff-ul pe corpul livrat ramane zero.
 */
export default function LoginPage() {
  return variantaLogin(AUTH_PAROLA_ACTIVA, AUTENTIFICARE_COD_ACTIVA) === 'cod'
    ? <LoginCuCod />
    : <LoginCuParola />;
}

/**
 * Varianta livrata din 21.09: e-mail + «Trimite-mi cod de autentificare prin email», plus
 * intrarea de inscriere.
 *
 * 🔴 UN SINGUR PAS (punctul 0 din lista owner-ului, 19.09). Inainte, butonul de aici doar valida
 * local si naviga la `/cod-de-acces`, iar `CerereCodPage` reia e-mailul si abia EL chema
 * `POST /api/auth/otp/request`. Omul tasteza adresa, apasa, ajunge pe un al doilea ecran care
 * arata exact ce a tastat, si apasa din nou — pentru un singur pas. Acum cererea pleaca de aici,
 * iar la succes se sare DIRECT la `/cod-de-acces/verificare`.
 *
 * `/cod-de-acces` ramane in cod, cu pagina ei: cele doua linkuri din `VerificareCodPage`
 * („Cere un cod", fara `state`, si «Schimbă», cu `state`) si `LoginCuParola` (dormanta) trec prin
 * ea. Butonul „Accesează" din `IntrariInscriere` a fost sters pe 20.09 — ducea exact in acelasi
 * loc in care duce formularul de aici, deci era un pas in plus, nu o a doua cale.
 *
 * ⚠️ Cheia din `state` e `identificator`, nu `email` — asa o citeste `VerificareCodPage`. Aici a
 * fost odata `email`, deci precompletarea nu s-a facut niciodata. Forma se scrie acum o singura
 * data, in `stareVerificareCod` (`utils/otp-cod.ts`).
 *
 * ⚠️ `type="text"` + `inputMode="email"`, nu `type="email"`: validarea nativa a browserului ar
 * bloca `submit`-ul INAINTE de handler, deci mesajul propriu n-ar apuca sa se randeze. Acelasi
 * motiv, scris pe larg, in `CerereCodPage`.
 *
 * 🔴 Din 21.09, a doua cerere a owner-ului: `/autentificare` e DOAR alegerea — titlul „Profesori” si
 * cele doua intrari una sub alta (`LoginCuCod` de mai jos). Formularul descris aici s-a mutat, intact,
 * pe `/autentificare/email` (`AutentificareEmailPage`), cu «← Înapoi» sus.
 *
 * Din 21.09 (item 1): intrarile «Autentificare» si «Înscriere» (`/cod-de-acces`) au inlocuit
 * blocul „Ești profesor nou? / Înscrie-te" de sub formular: profesorul nou ajunge acum la
 * inregistrare prin «Înscriere» → „nu exista cont" → «Înregistrează școala nouă», cu emailul deja
 * scris. `IntrariInscriere` ramane pe `HomePage`, neatinsa.
 *
 * Elevul trimis inapoi de pe «Înscriere» (are cont de ELEV, deci nu i-a plecat niciun cod) vine cu
 * `state: { email, mesaj }`: emailul se precompleteaza, mesajul se afiseaza deasupra campului.
 */
function LoginCuCod() {
  return (
    <PageWrap>
      {/* `zoom: 1.1`: cardul arata ca la 110% in Chrome (decizia owner-ului, 21.09) — text, butoane si
          spatieri, toate odata. `zoom` mareste si latimea de layout, deci latimea se imparte la 1.1,
          altfel la 360px cardul ar iesi din ecran. */}
      <Card className="mx-auto w-[calc(100%/1.1)] bg-card/90 backdrop-blur-sm [zoom:1.1]">
        <CardHeader className="text-center">
          {/* Titlul „Profesori” in stilul etichetei „Email” de pe pasul urmator (decizia owner-ului,
              21.09): aceeasi familie si greutate, doar mai mare. */}
          <h1 className="form-label-bold m-0 text-2xl">Profesori</h1>
        </CardHeader>
        <CardContent>
          <AlegereIntrare />
        </CardContent>
        {/* Separatorul in propriul `div` cu padding: `w-full` din `Separator` + `mx-6` il scoteau din card. */}
        <div className="px-6">
          <Separator />
        </div>
        <CardHeader className="text-center">
          <h2 className="form-label-bold m-0 text-2xl">Elevi</h2>
        </CardHeader>
        <CardContent>
          <AlegereElevi />
        </CardContent>
        <InParteneriatCu className="mt-3 border-t border-hairline pt-2.5 sm:mt-5 sm:pt-4" />
      </Card>
    </PageWrap>
  );
}

/**
 * Pasul 2 al «Autentificare», pe `/autentificare/email` (din 21.09): formularul de email care statea
 * direct pe `/autentificare`, mutat intact. Sus, «← Înapoi» spre alegere.
 */
export function AutentificareEmailPage() {
  const { PATHS, PAGES } = useRoutesProvider();
  const navigate = useNavigate();
  const location = useLocation();
  const venitDinInscriere = location.state as { email?: unknown; mesaj?: unknown } | null;
  const [email, setEmail] = React.useState(() =>
    typeof venitDinInscriere?.email === 'string' ? venitDinInscriere.email : '');
  const mesajInscriere = typeof venitDinInscriere?.mesaj === 'string' ? venitDinInscriere.mesaj : null;
  const [{ seTrimite, eroare }, dispatch] = React.useReducer(reduceTrimitere, STARE_TRIMITERE_INITIALA);
  const campRef = React.useRef<HTMLInputElement>(null);

  async function trimite(e: React.FormEvent) {
    e.preventDefault();
    // Un al doilea `submit` cat timp cererea e in zbor e ignorat. Butonul e `aria-busy`, nu
    // `disabled` (motivul e la buton), deci click-ul chiar ajunge aici.
    if (seTrimite) return;
    /* `incearca` INAINTE de validarea de client: altfel alerta de server ramane pe ecran peste
       un refuz al nostru, si pare ca s-a mai trimis o data degeaba (vezi `stare-trimitere.ts`). */
    dispatch({ tip: 'incearca' });
    if (!esteEmailValid(email)) {
      dispatch({ tip: 'esuat', mesaj: 'Introdu adresa de email a contului.' });
      campRef.current?.focus();
      return;
    }
    dispatch({ tip: 'pleaca' });
    try {
      const mesaj = await cereCodOtp(email.trim());
      navigate(PATHS[PAGES.VERIFICARE_COD], { state: stareVerificareCod(email, mesaj, Date.now(), 'autentificare') });
    } catch (err) {
      dispatch({ tip: 'esuat', mesaj: getErrorMessage(err, 'Nu am putut cere codul. Încearcă din nou.') });
    } finally {
      dispatch({ tip: 'terminat' });
    }
  }

  return (
    <PageWrap>
      <h1 className="sr-only">Autentificare</h1>
      {/* `bg-card/90 backdrop-blur-sm`: fotografia de fundal se vede prin card, dar textul ramane
          peste prag. AICI, pe cardul ecranului, nu intr-o regula globala — vezi SE-01. */}
      <Card className="bg-card/90 backdrop-blur-sm">
        {/* `text-center` pe form: cardul era centrat pe orizontala, dar continutul lui statea la
            stanga. Inputul isi tine centrarea din `form-input-styled` (`main.css`), butonul e
            `w-full` — nimic nou in CSS. Headerul ramane aliniat la stanga, ca «Înapoi». */}
        <CardHeader>
          <InapoiLaAlegere />
        </CardHeader>
        <CardContent>
          {/* `role="status"`: mesajul apare odata cu ecranul, dupa o navigare, deci nu e o
              eroare — e explicatia pentru care omul a ajuns aici in loc de ecranul de cod. */}
          {mesajInscriere && (
            <p role="status" className="mb-4 mt-0 rounded-lg border border-hairline bg-light p-3 text-center text-small text-dark">
              {mesajInscriere}
            </p>
          )}
          <form onSubmit={trimite} className="space-y-4 text-center">
            <div className="space-y-2">
              {/* Explicatia sta pe al doilea rand al etichetei, nu pe un paragraf separat: acolo
                  e clipa in care omul se intreaba „unde e parola?", iar un rand de text liber
                  intre camp si buton se citeste ca si cum ar fi optional. */}
              <label htmlFor="login-email" className="form-label-bold">
                Email
                <span className="block font-normal text-gray-600">
                  (În loc de parolă vei primi un cod unic pe e-mail)
                </span>
              </label>
              <Input
                id="login-email"
                ref={campRef}
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  // Reducer-ul intoarce ACEEASI referinta cand nu era nicio eroare, deci React
                  // nu re-randeaza degeaba la fiecare tasta.
                  if (eroare) dispatch({ tip: 'incearca' });
                }}
                placeholder="exemplu@email.ro"
                className="form-input-styled"
                aria-required="true"
                aria-invalid={!!eroare}
              />
            </div>

            {eroare && (
              <p role="alert" data-eroare-login className="m-0 rounded-lg border border-error bg-error/10 p-3 text-small text-error">
                {eroare}
              </p>
            )}

            {/* `aria-busy`, NU `disabled`: `buttonVariants` are `disabled:opacity-50`, iar o
                eticheta stearsa la 50% cade sub pragul de contrast. Starea se vede din eticheta
                («Se trimite…»), iar dublul submit e oprit de garda din `trimite()`. Aceeasi
                decizie ca pe `CerereCodPage`. */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              aria-busy={seTrimite}
              className="w-full touch-target"
            >
              {seTrimite ? 'Se trimite…' : 'Trimite-mi cod de autentificare prin email'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </PageWrap>
  );
}

/** Ecranul de dinainte de 21.09, neatins. Se randeaza doar cu `VITE_AUTH_PAROLA='true'`. */
function LoginCuParola() {
  const { PATHS, PAGES } = useRoutesProvider();
  const navigate = useNavigate();
  const { form, onSubmit, loading, escalation, showReenrollHint, loginError } = useLoginWithForm();
  const { suggestion, applySuggestion, dismissSuggestion, isEmailRoDomain, checkEmail } =
    useEmailTypoDetection(form, 'email');

  // Stare de baza (fara esec de login) = link text, nu buton plin - nu mai concureaza vizual cu
  // "Autentificare" sau cu butonul Google. Escaladarile (warning/primary-full) raman butoane pline,
  // intentionat: acolo chiar vrem sa fie greu de ratat, dupa esecuri repetate de login.
  const forgotPasswordVariant =
    escalation.forgotPasswordVariant === 'default' ? 'link' : 'default';
  const forgotPasswordClassName = (() => {
    switch (escalation.forgotPasswordVariant) {
      case 'warning':
        return 'bg-warning text-white animate-pulse touch-target';
      case 'primary-full':
        return 'w-full bg-primary text-white text-lg touch-target';
      default:
        return 'touch-target text-fluid-sm';
    }
  })();

  return (
    <PageWrap>
      {/* Pagina are doua carduri co-egale (inregistrare + conectare); niciunul nu e titlul
          intregii pagini, deci titlul real e separat, nu vizibil (pagina n-are nevoie de el
          vizual — cele doua carduri se explica singure). */}
      <h1 className="sr-only">Autentificare</h1>
      {/* Registration Card */}
      {/* SE-01: `bg-card/90 backdrop-blur-sm` — fotografia de fundal se vede prin card, dar textul
          ramane peste prag. AICI, pe cele doua carduri ale ecranului, nu intr-o regula globala:
          `Card` e folosit de zeci de pagini care NU stau pe fotografie, iar acolo translucidul
          n-ar avea ce sa lase sa se vada si ar strica contrastul degeaba. `main.css` neatins.
          Contrastele masurate, pe pixelul cel mai inchis al fotografiei, sunt in
          `docs/superpowers/progress/2026-09-mecanism-2027/S3.md`. */}
      <Card className="mb-4 bg-card/90 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-fluid-xl font-heading">Înregistrare</CardTitle>
          <CardDescription className="text-fluid-sm">Alege tipul de cont pentru a te înregistra</CardDescription>
        </CardHeader>
        <CardContent>
          {/* P1-7: `mobile-stack` (main.css) e flex fara justify-content; butoanele
              n-au w-full, deci flex-start implicit le lipeste la stanga, sub cardul
              centrat. `justify-center` aici, pe consumator -- main.css e denylist,
              iar defectul e de aplicare intr-o pagina, nu o regula gresita in
              utility-ul partajat. Singurul alt consumator (`GoogleSignInButton`)
              nu s-a atins -- neverificat daca are acelasi simptom. */}
          <div className="mobile-stack gap-responsive justify-center">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              disabled={!FEATURE_FLAGS.PROFESSOR_REGISTRATION.enabled || loading}
              onClick={() => navigate(PATHS[PAGES.TEACHER_REGISTER])}
              className={`touch-target ${!FEATURE_FLAGS.PROFESSOR_REGISTRATION.enabled ? "cursor-not-allowed opacity-50" : ""}`}
              title={!FEATURE_FLAGS.PROFESSOR_REGISTRATION.enabled ? FEATURE_FLAGS.PROFESSOR_REGISTRATION.disabledMessage : ""}
            >
              Înregistrare Profesor
            </Button>
            <Button
              type="button"
              variant="default"
              size="lg"
              disabled={loading}
              onClick={() => navigate(PATHS[PAGES.PUPIL_REGISTER])}
              className="touch-target"
            >
              Înregistrare Elev
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Login Card */}
      {/* Aceleasi doua clase ca pe cardul de inregistrare — vezi motivul de mai sus. */}
      <Card className="bg-card/90 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-fluid-xl font-heading">Conectare</CardTitle>
          <CardDescription className="text-fluid-sm">Introdu datele de autentificare</CardDescription>
        </CardHeader>
        <CardContent>
          {isGoogleSignInAvailable() && (
            <div className="mb-4 space-y-4">
              <GoogleSignInButton />
              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-fluid-sm text-muted-foreground">sau</span>
                <Separator className="flex-1" />
              </div>
            </div>
          )}
          <Form {...form}>
            <form id="login-form" onSubmit={onSubmit} autoComplete="on" className="space-y-3 md:space-y-4">
              <div className="grid gap-3 md:gap-4">
                {/* Email Field */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">Email:</FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              type="email"
                              name="email"
                              autoComplete="username"
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              placeholder="exemplu@email.ro"
                              className="form-input-styled"
                              aria-required="true"
                              onBlur={(e) => {
                                field.onBlur();
                                checkEmail(e.target.value);
                              }}
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                          {suggestion && (
                            <p className="text-sm text-warning mt-1">
                              Ai vrut să scrii{' '}
                              <button
                                type="button"
                                className="my-1.5 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle font-semibold underline text-magenta hover:text-magenta-hover md:my-0 md:min-h-0 md:min-w-0 md:px-0"
                                onClick={applySuggestion}
                              >
                                {suggestion}
                              </button>
                              ?{' '}
                              <button
                                type="button"
                                className="my-1.5 ml-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 align-middle text-xs text-muted-foreground hover:underline md:ml-0 md:my-0 md:min-h-0 md:min-w-0 md:px-0"
                                onClick={dismissSuggestion}
                              >
                                Nu
                              </button>
                            </p>
                          )}
                          {isEmailRoDomain && !suggestion && (
                            <p className="text-sm text-warning mt-1">
                              ⚠ Adresa @email.ro nu este un furnizor real de email. Verifică adresa.
                            </p>
                          )}
                        </div>
                      </FormItem>
                    </div>
                  )}
                />

                {/* Password Field */}
                <FormField
                  control={form.control}
                  name="parola"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">Parola:</FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <PasswordInput
                              {...field}
                              name="password"
                              autoComplete="current-password"
                              className="form-input-styled"
                              aria-required="true"
                              forceVisible={escalation.forcePasswordVisible}
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    </div>
                  )}
                />

                {/* Escalation messages */}
                {escalation.suggestReset && (
                  <p className="text-sm text-warning font-medium text-center">
                    Pare că ai uitat parola. Vrei să o resetezi?
                  </p>
                )}
                {escalation.showTeacherHelp && (
                  <p className="text-sm text-error font-medium text-center">
                    Cere ajutor de la profesor pentru a-ți reseta parola.
                  </p>
                )}

                {/* `form-field-row` cu un spacer gol in coloana de eticheta (ascuns sub
                    768px, unde grila oricum e pe o singura coloana) -- altfel butonul
                    incepe cu 224px mai la stanga decat campurile, de sub eticheta de
                    200px + gap (constatarea #7, S1). */}
                <div className="form-field-row">
                  <span aria-hidden className="hidden md:block" />
                  {/* Eroarea de login sta IN randul butonului, deasupra lui, ca un rand de text in
                      stilul `FormMessage` (S1, 2026-09-15). Nu toast: fixat sus, era la 431px de
                      buton la 360x649. Nu `Alert`: cardul avea 66px si impingea butonul sub ecran.
                      Nici rand propriu `form-field-row`: padding-ul lui adauga 16px si butonul tot
                      iesea cu 10px. Aici costa 20px + gap 8px. */}
                  <div className="flex flex-col gap-2">
                    {loginError && (
                      <p role="alert" className="m-0 text-sm text-destructive">
                        {loginError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      disabled={loading || escalation.remainingCooldown > 0}
                      className="w-full touch-target"
                    >
                      {escalation.remainingCooldown > 0
                        ? `Așteaptă ${escalation.remainingCooldown}s...`
                        : loading
                          ? 'Se autentifică...'
                          : 'Autentificare'}
                    </Button>
                  </div>
                </div>

                {/* Elevii din editia trecuta primesc tot 401 la login. Nu redirectam automat:
                    acelasi 401 apare si cand emailul e tastat gresit. */}
                {showReenrollHint && (
                  <p className="text-fluid-sm text-center">
                    Ai avut cont în ediția trecută?{' '}
                    <button
                      type="button"
                      className="my-1.5 inline-flex min-h-11 items-center justify-center rounded-md px-2 align-middle text-base font-semibold underline hover:text-primary md:my-0 md:min-h-0 md:px-0 md:text-fluid-sm"
                      onClick={() =>
                        navigate(PATHS[PAGES.PUPIL_REENROLL], {
                          state: {
                            email: form.getValues('email'),
                            parola: form.getValues('parola'),
                          },
                        })
                      }
                    >
                      Reînscrie-te în ediția curentă
                    </button>
                  </p>
                )}
              </div>
            </form>
          </Form>

          {/* Intrarea cu cod (T1), sub flag: fara flag ruta nici nu se inregistreaza,
              deci legatura ar fi moarta — de-aia sta tot sub flag.
              Dupa T1, `/api/auth/otp/verify` intoarce o SESIUNE, nu un token de
              resetare, deci textul poate spune „intri fara parola" fara sa minta.
              🔴 `variant="outline"` e fix, nu calculat: cand `forgotPasswordVariant`
              escaladeaza dupa trei esecuri (warning / primary-full), «Am uitat parola»
              devine buton plin — iar doua butoane pline de latime intreaga pe acelasi
              ecran se concureaza. Conturatul ramane conturat, prin constructie. */}
          {AUTENTIFICARE_COD_ACTIVA && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-fluid-sm text-muted-foreground">sau</span>
                <Separator className="flex-1" />
              </div>
              {/* 🔴 Intrebarea de deasupra butonului, nu in eticheta lui: dupa b24 pe ecranul
                  asta sunt DOUA intrari care contin cuvantul „cod", si ele nu se disting prin
                  fel de cod, ci prin CINE ESTI (S1, directia (d) §1). Un colaborator care are
                  codul scolii ar apasa aici, fiindca si aici scrie „cod", si ar astepta un SMS
                  care nu vine. Intrebarea „am cont sau nu?" il duce singura pe calea buna.
                  Stau deasupra, nu in eticheta, ca butoanele sa nu creasca la 360 px. */}
              <p className="m-0 text-fluid-sm font-semibold text-dark">Ai un cont?</p>
              {/* `form-field-row` cu spacer gol -- aceeasi coloana ca inputurile si
                  «Autentificare» la >=768px (constatarea #7, S1, runda 4: acest buton
                  ramasese pe [281,999], nu [505,991]). */}
              <div className="form-field-row">
                <span aria-hidden className="hidden md:block" />
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={loading}
                  onClick={() => navigate(PATHS[PAGES.CERERE_COD], {
                    // 🔴 Cheia e `identificator`, cum o citeste `CerereCodPage`. Aici era
                    //    `email`, deci precompletarea nu s-a facut niciodata: campul e unul
                    //    singur, „telefon sau email", si asa se numeste si in state.
                    state: { identificator: form.getValues('email') ?? '' },
                  })}
                  className="w-full touch-target"
                >
                  Intră cu un cod, fără parolă
                </Button>
              </div>
            </div>
          )}

          {/* Intrarea colaboratorului fara cont. SUB cea de OTP, deliberat: majoritatea celor care
              ajung pe login au cont, iar colaboratorul e cazul mai rar si vine cu instructiuni de
              la profesor.
              ⚠️ Separatorul «sau» apare aici DOAR cand blocul de OTP lipseste — altfel ar fi doua
              separatoare unul sub altul. Cele doua flaguri sunt independente si pot fi pornite in
              orice combinatie, deci cazul se scrie, nu se presupune. */}
          {INTRARE_COD_SCOALA_ACTIVA && (
            <div className="mt-4 space-y-4">
              {!AUTENTIFICARE_COD_ACTIVA && (
                <div className="flex items-center gap-3">
                  <Separator className="flex-1" />
                  <span className="text-fluid-sm text-muted-foreground">sau</span>
                  <Separator className="flex-1" />
                </div>
              )}
              <p className="m-0 text-fluid-sm font-semibold text-dark">Nu ai cont?</p>
              {/* Acelasi tipar ca butonul de mai sus -- aliniere pe coloana campurilor
                  la >=768px (constatarea #7). */}
              <div className="form-field-row">
                <span aria-hidden className="hidden md:block" />
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={loading}
                  onClick={() => navigate(PATHS[PAGES.INTRARE_COD_SCOALA])}
                  className="w-full touch-target"
                >
                  Intră cu codul primit de la școală
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            type="button"
            variant={forgotPasswordVariant}
            size="lg"
            disabled={loading}
            onClick={() => navigate(PATHS[PAGES.FORGOTTEN_PASSWORD])}
            className={forgotPasswordClassName}
          >
            Am uitat parola
          </Button>
        </CardFooter>
      </Card>
    </PageWrap>
  );
}
